import { useEffect, useRef } from "react";

import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { IntegrationSyncStatus } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { Timer } from "app/pages/sales-intelligence/types";

const POLL_INTERVAL_MS = 3500;
const MAX_REFRESH_STATUS_RETRIES = 5;

type UseCRMStatusPollingParams = {
    syncStatus: IntegrationSyncStatus | undefined;
    onRefetch: () => Promise<IntegrationSyncStatus>;
    onSettled?: (status: IntegrationSyncStatus) => void;
};

/**
 * Polls the integration status while it is syncing (InProgress/New), mirroring the legacy
 * IntegrationSection behaviour. It is intentionally dumb: it owns only a local retry counter and
 * delegates fetching to `onRefetch`, so it knows nothing about the integration, store or thunks.
 */
export const useCRMStatusPolling = ({ syncStatus, onRefetch, onSettled }: UseCRMStatusPollingParams): void => {
    const retryCountRef = useRef(0);

    const onRefetchRef = useRef(onRefetch);
    const onSettledRef = useRef(onSettled);
    onRefetchRef.current = onRefetch;
    onSettledRef.current = onSettled;

    const isSyncing = syncStatus === INTEGRATION_STATUS.InProgress || syncStatus === INTEGRATION_STATUS.New;

    useEffect(() => {
        if (!isSyncing) {
            retryCountRef.current = 0;
            return;
        }

        let isCancelled = false;
        let timer: Timer;

        const scheduleRefresh = () => {
            timer = setTimeout(async () => {
                if (retryCountRef.current >= MAX_REFRESH_STATUS_RETRIES) {
                    return;
                }

                const newStatus = await onRefetchRef.current();

                if (isCancelled) {
                    return;
                }

                if (newStatus === INTEGRATION_STATUS.InProgress || newStatus === INTEGRATION_STATUS.New) {
                    retryCountRef.current += 1;
                    scheduleRefresh();
                } else {
                    retryCountRef.current = 0;
                    onSettledRef.current?.(newStatus);
                }
            }, POLL_INTERVAL_MS);
        };

        scheduleRefresh();

        return () => {
            isCancelled = true;
            clearTimeout(timer);
        };
    }, [isSyncing]);
};
